import React, { useCallback, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
} from '@patternfly/react-core';
import { ActionsColumn, type IAction, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, HandshakeIcon, PlusCircleIcon, SyncAltIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useUserContext } from '../../../contexts/user/UserContext';
import { useImpersonation } from '../../../contexts/impersonation/ImpersonationContext';
import { useAdminSpecials } from '../../../hooks/useAdminSpecials';
import { useClientSpecials } from '../../../hooks/useClientSpecials';
import type { AdminSpecialDeal } from '../../../types/models/SpecialDeal';
import type {
  ApiResponseAdminSpecialDelete,
  ApiResponseAdminSpecialUpdate,
} from '../../../types/api/ApiResponseSpecialDeal';
import SpecialDealModal from './SpecialDealModal';
import {
  formatDealCadence,
  formatDealValue,
  formatOccurrencesLeft,
  renderDealKindBadge,
} from './specialDealDisplay';

/**
 * Organization → Agreements: the special billing agreements negotiated for the in-scope
 * organization. This page shows STANDALONE agreements only (`parent_type: null`) — agreements
 * attached to a product (e.g. a virtual machine) render on that product's own page instead.
 *
 * Two variants share the page:
 *   • Clients get a read-only table from GET /clients/specials (ACTIVE deals, no internals).
 *   • Ancalagon staff (gated on the REAL user's `is_ancalagon_staff`, which survives
 *     impersonation) get the richer /admin/billing/specials list for the impersonated
 *     organization — internal note, active flag, billed-event count — plus create / edit /
 *     deactivate / delete. The admin API takes the clientID in the request BODY, so the
 *     impersonation target's id is passed explicitly.
 */
function Agreements() {
  const auth = useAuth();
  const user = useUserContext();
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { target } = useImpersonation();

  const isStaff = user?.is_ancalagon_staff === true;
  // The billed organization: the impersonation target, or the user's own org outside impersonation.
  const clientId = target?.id ?? user?.clientID ?? null;
  const clientName = target?.name ?? null;

  // Exactly one of the two lists is fetched: staff read the admin list (needs the note /
  // billed_event_count for the management affordances), clients the tenant read.
  const admin = useAdminSpecials(clientId, isStaff);
  const client = useClientSpecials(!isStaff);
  const loading = isStaff ? admin.loading : client.loading;
  const error = isStaff ? admin.error : client.error;
  const refetch = isStaff ? admin.refetch : client.refetch;

  // Standalone agreements only — attached rows belong to their parent's page.
  const staffRows = useMemo(() => admin.specials.filter((d) => d.parent_type === null), [admin.specials]);
  const clientRows = useMemo(() => client.specials.filter((s) => s.parent_type === null), [client.specials]);
  const rowCount = isStaff ? staffRows.length : clientRows.length;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editDeal, setEditDeal] = useState<AdminSpecialDeal | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminSpecialDeal | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeactivate = useCallback(
    async (deal: AdminSpecialDeal) => {
      try {
        const response = await apiFetch(`/admin/billing/specials/${deal.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ active: false }),
        });
        // Branch on the JSend body.status, NEVER response.ok.
        const body = (await response.json().catch(() => null)) as ApiResponseAdminSpecialUpdate | null;
        if (body?.status === 'success' && body.data?.special) {
          addNotification({
            title: 'Agreement deactivated',
            srTitle: 'Success alert',
            description: `${deal.product.label} no longer emits invoice lines. An already-emitted pending line still lands on its invoice.`,
            variant: 'success',
          });
          setDrawerOpen(true);
          void refetch();
        } else {
          addNotification({
            title: 'Could not deactivate agreement',
            srTitle: 'Error alert',
            description: body?.data?.active ?? body?.data?.body ?? body?.message ?? 'Could not deactivate the agreement.',
            variant: 'danger',
          });
          setDrawerOpen(true);
        }
      } catch (err) {
        addNotification({
          title: 'Could not deactivate agreement',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      }
    },
    [apiFetch, addNotification, setDrawerOpen, refetch],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) {
      return;
    }
    setIsDeleting(true);
    try {
      const response = await apiFetch(`/admin/billing/specials/${deleteTarget.id}`, { method: 'DELETE' });
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseAdminSpecialDelete | null;
      if (body?.status === 'success' && (body.data?.deleted === true || body.data?.deactivated === true)) {
        // The API decides: hard delete while never billed, deactivate (kept for audit) otherwise.
        addNotification({
          title: body.data.deleted ? 'Agreement deleted' : 'Agreement deactivated',
          srTitle: 'Success alert',
          description: body.data.deleted
            ? `${deleteTarget.product.label} was never billed and has been permanently deleted.`
            : `${deleteTarget.product.label} has billed events on record — it was deactivated and kept for audit instead of deleted.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        setDeleteTarget(null);
        void refetch();
      } else {
        addNotification({
          title: 'Could not delete agreement',
          srTitle: 'Error alert',
          description: body?.message ?? 'Could not delete the agreement. Please try again.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      }
    } catch (err) {
      addNotification({
        title: 'Could not delete agreement',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsDeleting(false);
    }
  }, [apiFetch, addNotification, setDrawerOpen, deleteTarget, refetch]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const orgLabel = clientName ?? 'your organization';

  const staffRowActions = (deal: AdminSpecialDeal): IAction[] => [
    {
      title: 'Modify',
      onClick: () => setEditDeal(deal),
    },
    {
      title: 'Deactivate',
      isAriaDisabled: !deal.active,
      ...(deal.active
        ? {}
        : { tooltipProps: { content: 'This agreement is already inactive' } }),
      onClick: () => void handleDeactivate(deal),
    },
    { isSeparator: true },
    {
      title: 'Delete',
      onClick: () => setDeleteTarget(deal),
    },
  ];

  const renderStaffTable = () => (
    <Table aria-label="Special agreements list" variant="compact">
      <Thead>
        <Tr>
          <Th>Agreement</Th>
          <Th>Kind</Th>
          <Th>Value</Th>
          <Th>Billing</Th>
          <Th>Remaining</Th>
          <Th>Status</Th>
          <Th screenReaderText="Row actions" />
        </Tr>
      </Thead>
      <Tbody>
        {staffRows.map((deal) => (
          <Tr key={deal.id}>
            <Td dataLabel="Agreement">
              <div>{deal.product.label}</div>
              {deal.note !== null && <Content component="small">{deal.note}</Content>}
            </Td>
            <Td dataLabel="Kind">{renderDealKindBadge(deal.kind)}</Td>
            <Td dataLabel="Value">{formatDealValue(deal.kind, deal.value)}</Td>
            <Td dataLabel="Billing">{formatDealCadence(deal.product.recurring, deal.next_billing_at)}</Td>
            <Td dataLabel="Remaining">{formatOccurrencesLeft(deal.occurrences_left)}</Td>
            <Td dataLabel="Status">
              {deal.active ? (
                <Label color="green" isCompact>
                  Active
                </Label>
              ) : (
                <Label color="grey" isCompact>
                  Inactive
                </Label>
              )}{' '}
              <Content component="small">
                {deal.billed_event_count} billed event{deal.billed_event_count === 1 ? '' : 's'}
              </Content>
            </Td>
            <Td isActionCell>
              <ActionsColumn items={staffRowActions(deal)} />
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );

  const renderClientTable = () => (
    <Table aria-label="Special agreements list" variant="compact">
      <Thead>
        <Tr>
          <Th>Agreement</Th>
          <Th>Kind</Th>
          <Th>Value</Th>
          <Th>Billing</Th>
          <Th>Remaining</Th>
        </Tr>
      </Thead>
      <Tbody>
        {clientRows.map((agreement) => (
          <Tr key={agreement.id}>
            <Td dataLabel="Agreement">{agreement.product.label ?? '—'}</Td>
            <Td dataLabel="Kind">{renderDealKindBadge(agreement.kind)}</Td>
            <Td dataLabel="Value">{formatDealValue(agreement.kind, agreement.value)}</Td>
            <Td dataLabel="Billing">{formatDealCadence(agreement.product.recurring, agreement.next_billing_at)}</Td>
            <Td dataLabel="Remaining">{formatOccurrencesLeft(agreement.occurrences_left)}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );

  const renderContent = () => {
    // Full-page spinner only on the genuine initial load; a manual Refresh keeps the table visible.
    if (loading && rowCount === 0) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load agreements"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (rowCount === 0) {
      return (
        <EmptyState titleText="No special agreements" icon={HandshakeIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            {isStaff
              ? `No standalone special agreements exist for ${orgLabel}.`
              : 'Your organization has no standalone special agreements. Agreements attached to a specific product are shown on that product’s page.'}
          </EmptyStateBody>
          {isStaff && (
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button
                  variant="primary"
                  icon={<PlusCircleIcon />}
                  onClick={() => setIsCreateOpen(true)}
                  isDisabled={clientId === null}
                >
                  Create agreement
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          )}
        </EmptyState>
      );
    }

    return isStaff ? renderStaffTable() : renderClientTable();
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem>Organization</BreadcrumbItem>
          <BreadcrumbItem isActive>Agreements</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} alignItems={{ default: 'alignItemsFlexStart' }}>
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <HandshakeIcon />
              </Icon>
              Agreements
            </Content>
            <Content component="p">
              Special agreements negotiated for your organization — recurring services and rebates that appear
              as extra lines on your invoices. Agreements attached to a specific product (e.g. a virtual
              machine) are shown on that product&apos;s page; your billing details live under Billing, the
              standard price catalog under Prices, and the resulting invoices under Invoices.
            </Content>
          </FlexItem>
          <FlexItem>
            <Flex spaceItems={{ default: 'spaceItemsSm' }}>
              {/* Staff-only create (standalone agreements are charge-only; rebates are created from
                  their parent's page). Hidden only during the genuine initial load. */}
              {isStaff && !(loading && rowCount === 0) && (
                <FlexItem>
                  <Button
                    variant="primary"
                    icon={<PlusCircleIcon />}
                    onClick={() => setIsCreateOpen(true)}
                    isDisabled={clientId === null}
                  >
                    Create agreement
                  </Button>
                </FlexItem>
              )}
              <FlexItem>
                <Button
                  variant="secondary"
                  icon={<SyncAltIcon />}
                  onClick={() => void refetch()}
                  isLoading={loading}
                  isDisabled={loading}
                >
                  Refresh
                </Button>
              </FlexItem>
            </Flex>
          </FlexItem>
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>
      {isCreateOpen && clientId !== null && (
        <SpecialDealModal
          isOpen
          onClose={() => setIsCreateOpen(false)}
          onSaved={() => void refetch()}
          clientId={clientId}
          clientName={clientName}
        />
      )}
      {editDeal && clientId !== null && (
        <SpecialDealModal
          key={editDeal.id}
          isOpen
          deal={editDeal}
          onClose={() => setEditDeal(null)}
          onSaved={() => void refetch()}
          clientId={clientId}
          clientName={clientName}
        />
      )}
      {deleteTarget && (
        <Modal
          variant="small"
          isOpen
          onClose={() => (isDeleting ? undefined : setDeleteTarget(null))}
          aria-label="Delete agreement confirmation"
        >
          <ModalHeader title="Delete agreement?" titleIconVariant="warning" />
          <ModalBody>
            {deleteTarget.billed_event_count > 0
              ? `“${deleteTarget.product.label}” has ${deleteTarget.billed_event_count} billed event${
                  deleteTarget.billed_event_count === 1 ? '' : 's'
                } on record, so it will be deactivated and kept for audit instead of deleted. Already-emitted pending lines still land on their invoice.`
              : `“${deleteTarget.product.label}” has never billed and will be permanently deleted.`}
          </ModalBody>
          <ModalFooter>
            <Button variant="danger" onClick={() => void handleDelete()} isLoading={isDeleting} isDisabled={isDeleting}>
              Delete
            </Button>
            <Button variant="link" onClick={() => setDeleteTarget(null)} isDisabled={isDeleting}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
}

export { Agreements };
